// src/components/users/ResetPasswordModal.js
// --- UPDATE (User Management) ------------------------------------------------------------
// Reset another user's password: FlowSense generates a strong temporary password (or the admin
// types one), "must change at next sign-in" on by default. The result is shown ONCE with copy.
// Also used for "Change my password" (mode="self": current password required).
// --- END UPDATE ---------------------------------------------------------------------------
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, Button, FormControl, FormLabel,
    Input, InputGroup, InputRightElement, IconButton, Icon, Switch, HStack, VStack, Text, Alert, AlertIcon, Box, Progress, Radio, RadioGroup, Stack,
    useColorModeValue, useClipboard,
} from '@chakra-ui/react';
import { MdLockReset, MdContentCopy, MdCheck, MdOutlineRemoveRedEye, MdRefresh } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';
import { passwordStrength, generatePassword } from './userAiIntel';

const ResetPasswordModal = ({ isOpen, onClose, user, mode = 'admin', onReset, passwordMinLength = 10 }) => {
    const [how, setHow] = useState('generate');
    const [pw, setPw] = useState('');
    const [current, setCurrent] = useState('');
    const [mustChange, setMustChange] = useState(true);
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const { hasCopied, onCopy, setValue } = useClipboard('');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const pwBg = useColorModeValue('gray.50', 'whiteAlpha.100');

    useEffect(() => { if (isOpen) { setHow(mode === 'self' ? 'type' : 'generate'); setPw(''); setCurrent(''); setMustChange(true); setShow(false); setError(null); setResult(null); } }, [isOpen, mode]);
    useEffect(() => { setValue(result?.temporary_password || ''); }, [result, setValue]);

    const strength = passwordStrength(pw, passwordMinLength);
    const needsTyped = mode === 'self' || how === 'type';

    const submit = async (e) => {
        e.preventDefault();
        if (needsTyped && !strength.ok) { setError(`Password needs ${strength.hints.filter((h) => !h.includes('symbol')).join(', ')}.`); return; }
        setBusy(true); setError(null);
        try {
            const r = await onReset(mode === 'self' ? { current_password: current, new_password: pw } : { password: how === 'type' ? pw : undefined, must_change_password: mustChange });
            if (mode === 'self') { onClose(); return; }
            setResult(r);
        } catch (err) { setError(err.message); } finally { setBusy(false); }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
            <ModalOverlay backdropFilter="blur(4px)" />
            <ModalContent as="form" onSubmit={submit} borderRadius="2xl">
                <ModalHeader>
                    <HStack><Icon as={MdLockReset} color="purple.500" /><Text>{mode === 'self' ? 'Change my password' : `Reset password · ${user?.display_name || user?.username || ''}`}</Text></HStack>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    {result ? (
                        <VStack align="stretch" spacing={3}>
                            <Alert status="success" borderRadius="lg"><AlertIcon />Password reset. Give it to {user?.display_name || user?.username} securely — it is shown only once.</Alert>
                            <HStack p={3} bg={pwBg} borderRadius="lg" justify="space-between">
                                <Text fontFamily="mono" fontSize="lg" fontWeight="800" letterSpacing="wider" aria-label="Temporary password">{result.temporary_password}</Text>
                                <Button size="sm" leftIcon={<Icon as={hasCopied ? MdCheck : MdContentCopy} />} onClick={onCopy}>{hasCopied ? 'Copied' : 'Copy'}</Button>
                            </HStack>
                            <Text fontSize="xs" color={muted}>{result.must_change_password ? 'They will be asked to choose their own password after signing in.' : 'They can keep this password.'}</Text>
                        </VStack>
                    ) : (
                        <VStack align="stretch" spacing={4}>
                            {error && <Alert status="error" borderRadius="lg"><AlertIcon />{error}</Alert>}
                            {mode === 'self' && (
                                <FormControl isRequired>
                                    <FormLabel fontSize="sm">Current password</FormLabel>
                                    <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
                                </FormControl>
                            )}
                            {mode !== 'self' && (
                                <RadioGroup value={how} onChange={setHow}>
                                    <Stack spacing={2}>
                                        <Radio value="generate">Generate a strong temporary password <Text as="span" fontSize="xs" color={muted}>(recommended)</Text></Radio>
                                        <Radio value="type">Type a password</Radio>
                                    </Stack>
                                </RadioGroup>
                            )}
                            {needsTyped && (
                                <FormControl isRequired>
                                    <FormLabel fontSize="sm">{mode === 'self' ? 'New password' : 'Password'}</FormLabel>
                                    <InputGroup>
                                        <Input type={show ? 'text' : 'password'} value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" pr="4.5rem" />
                                        <InputRightElement w="4.5rem">
                                            <HStack spacing={0}>
                                                <IconButton size="xs" variant="ghost" icon={<Icon as={MdRefresh} />} aria-label="Generate password" onClick={() => { setPw(generatePassword()); setShow(true); }} />
                                                <IconButton size="xs" variant="ghost" icon={<Icon as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye} />} aria-label={show ? 'Hide password' : 'Show password'} onClick={() => setShow((v) => !v)} />
                                            </HStack>
                                        </InputRightElement>
                                    </InputGroup>
                                    {pw && (
                                        <Box mt={2}>
                                            <Progress size="xs" borderRadius="full" value={(strength.score / 4) * 100} colorScheme={!strength.ok ? 'red' : strength.score >= 4 ? 'green' : 'yellow'} aria-label="Password strength" />
                                            <Text fontSize="xs" color={muted} mt={1}>{strength.label}{strength.hints.length ? ` · add ${strength.hints.join(', ')}` : ''}</Text>
                                        </Box>
                                    )}
                                </FormControl>
                            )}
                            {mode !== 'self' && (
                                <HStack>
                                    <Switch id="rp-mcp" isChecked={mustChange} onChange={(e) => setMustChange(e.target.checked)} colorScheme="purple" />
                                    <FormLabel htmlFor="rp-mcp" mb={0} fontSize="sm">Must change it at the next sign-in</FormLabel>
                                </HStack>
                            )}
                        </VStack>
                    )}
                </ModalBody>
                <ModalFooter gap={2}>
                    <Button variant="ghost" onClick={onClose}>{result ? 'Done' : 'Cancel'}</Button>
                    {!result && <Button type="submit" colorScheme="purple" isLoading={busy} leftIcon={<Icon as={MdLockReset} />}>{mode === 'self' ? 'Change password' : 'Reset password'}</Button>}
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

ResetPasswordModal.propTypes = { isOpen: PropTypes.bool, onClose: PropTypes.func.isRequired, user: PropTypes.object, mode: PropTypes.oneOf(['admin', 'self']), onReset: PropTypes.func.isRequired, passwordMinLength: PropTypes.number };

export default ResetPasswordModal;
